import {
  SESSION_SLICE_SESSION_STORAGE_KEY,
  axiosInstance,
  useDispatch,
  useSelector,
  updateToken,
  updateRefreshToken,
  logoutAuthUser,
  LoginTypeEnum,
  getLoggingActions,
  UserTypeEnum,
} from '@app-store'

import {
  AUTH_PROVIDER,
  getLoginCredentials,
  NO_TENANT_HEADER_API,
} from './constants'
import { environment } from './environment'
import type { InternalAxiosRequestConfig } from 'axios'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { jwtDecode } from 'jwt-decode'
import { recordSessionActivity } from '@ecrm-session'
import { getPostLogoutCapturePath } from './postLogoutReturnPath'

import { store, persist } from './store'
import { clearMsalAppSessionOnly, msalInstance } from './msalConfig'
import { clearMsalAccessTokenBridge } from '@ecrm-msal'
import { clearSessionLastTenantId, getTenantIdFromWindow } from '@app-store'

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()

export { store, persist }

// ──────────────────────────────────────────────
// Axios Request Interceptor
// Bearer token comes from Redux `session` (sessionStorage via redux-persist).
// ──────────────────────────────────────────────
axiosInstance.interceptors.request.use(function (
  config: InternalAxiosRequestConfig
) {
  const tenantId = getTenantIdFromWindow()
  const token = store.getState().session.token
  const configUrl: string = config.url ?? ''

  if (
    !NO_TENANT_HEADER_API.some((url) => configUrl?.includes(url)) &&
    tenantId != null &&
    !Object.prototype.hasOwnProperty.call(config.headers, 'X-TenantId')
  ) {
    config.headers['X-TenantId'] = tenantId === -1 ? '' : tenantId
  }

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }

  const markUser = config.countsAsUserActivity === true
  if (markUser) {
    delete config.countsAsUserActivity
  }
  if (markUser && token) {
    recordSessionActivity()
  }

  return config
})

// ──────────────────────────────────────────────
// Utility: Add dynamic params to login calls
// ──────────────────────────────────────────────
const dynamicParamConfig = (
  req: { url: string },
  newSource: string,
  userType?: string
) => {
  try {
    if (!req.url.includes('/v1/users/login')) return
    const base = req.url.startsWith('http')
      ? req.url
      : `${window.location.origin}${req.url}`
    const url = new URL(base)
    url.searchParams.set('action', newSource)

    if (userType) {
      url.searchParams.set('user_type', userType)
    }

    req.url = url.href.startsWith(window.location.origin)
      ? url.href.replace(window.location.origin, '')
      : url.href
  } catch (err) {
    console.error('Failed to update source param:', err)
  }
}

// ──────────────────────────────────────────────
// MSAL Singleton Setup
// ──────────────────────────────────────────────

// Initialize early
msalInstance.initialize().catch((e) => console.warn('MSAL init error:', e))

// ──────────────────────────────────────────────
// Token Refresh Concurrency Lock
// ──────────────────────────────────────────────
let tokenRefreshPromise: Promise<string | null> | null = null

// ──────────────────────────────────────────────
// Session Expiry Handler
// ──────────────────────────────────────────────
export const handleSessionExpiry = async (reason: string) => {
  console.error(`Session expired: ${reason}. Logging out...`)
  tokenRefreshPromise = null
  const token = store.getState().session.token
  if (token) {
    await getLoggingActions(
      token,
      LoginTypeEnum.ADMIN_FORCED_LOGOUT,
      UserTypeEnum.AGENT
    )
  }
  store.dispatch(logoutAuthUser())
  if (getLoginCredentials().authProvider === AUTH_PROVIDER.MSAL) {
    await clearMsalAppSessionOnly()
    clearMsalAccessTokenBridge()
  }
  clearSessionLastTenantId()
  await persist.flush()
  try {
    sessionStorage.removeItem(SESSION_SLICE_SESSION_STORAGE_KEY)
  } catch {
    /* ignore */
  }
  const capture = getPostLogoutCapturePath()
  const qs = new URLSearchParams()
  if (getLoginCredentials().authProvider === AUTH_PROVIDER.MSAL) {
    qs.set('forceMsalPromptNextLogin', '1')
  }
  if (capture) {
    qs.set('returnPath', capture)
  }
  const q = qs.toString()
  window.location.href = q
    ? `${window.location.origin}/logout?${q}`
    : `${window.location.origin}/logout`
}

// ──────────────────────────────────────────────
// MSAL Token Refresh Logic
// ──────────────────────────────────────────────
const refreshMSALToken = async (): Promise<string | null> => {
  console.log('Starting MSAL token refresh...')

  try {
    const accounts = msalInstance.getAllAccounts()
    console.log('MSAL accounts found:', accounts.length)

    if (!accounts || accounts.length === 0) {
      handleSessionExpiry('No MSAL accounts found in cache')
      return null
    }

    const account = msalInstance.getActiveAccount() ?? accounts[0]
    const request = {
      scopes: [
        'openid',
        'offline_access',
        'email',
        'profile',
        environment.msalConfig.msalScope,
      ],
      account,
      forceRefresh: false,
    }

    try {
      const response = await msalInstance.acquireTokenSilent(request)
      if (response && response.accessToken) {
        console.log('Token acquired silently, updating store')
        await getLoggingActions(
          response.accessToken,
          LoginTypeEnum.REFRESH,
          UserTypeEnum.AGENT
        )
        store.dispatch(updateToken({ token: response.accessToken }))
        return response.accessToken
      }
      console.warn('acquireTokenSilent returned no accessToken', response)
    } catch (err) {
      console.warn('acquireTokenSilent failed:', err)
      // Try to type-narrow err as best as possible rather than using 'any'
      // Most MSAL errors should be objects at runtime
      const errObj = err as { errorCode?: string; name?: string }
      const errCode = errObj?.errorCode || errObj?.name || ''
      const needsInteraction =
        err instanceof InteractionRequiredAuthError ||
        [
          'interaction_required',
          'login_required',
          'monitor_window_timeout',
          'consent_required',
        ].includes(errCode)

      if (!needsInteraction) {
        handleSessionExpiry('Token acquisition failed: ' + String(err))
        return null
      }

      console.log('Interaction required, attempting popup token acquisition...')
      try {
        console.log('Attempting popup token acquisition...')
        const popupResponse = await msalInstance.acquireTokenPopup(request)
        if (popupResponse && popupResponse.accessToken) {
          console.log('Token acquired via popup, updating store')
          await getLoggingActions(
            popupResponse.accessToken,
            LoginTypeEnum.REFRESH,
            UserTypeEnum.AGENT
          )
          store.dispatch(updateToken({ token: popupResponse.accessToken }))
          return popupResponse.accessToken
        } else {
          console.warn('Popup returned no token, fallback to redirect...')
          await msalInstance.acquireTokenRedirect(request)
          return null
        }
      } catch (popupErr) {
        console.error('Popup token acquisition failed:', popupErr)
        try {
          await msalInstance.acquireTokenRedirect(request)
          return null
        } catch (redirErr) {
          console.error('Redirect fallback failed:', redirErr)
          handleSessionExpiry(
            'Interactive token acquisition failed: ' + String(redirErr)
          )
          return null
        }
      }
    }

    return null
  } catch (e) {
    console.error('Token refresh failed with exception:', e)
    handleSessionExpiry('Token refresh exception: ' + String(e))
    return null
  }
}

// ──────────────────────────────────────────────
// Axios Response Interceptor (Token Refresh Logic)
// ──────────────────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    if (!originalRequest) return Promise.reject(error)

    type CustomAxiosRequestConfig = typeof originalRequest & {
      _retry?: boolean
    }
    const customRequest = originalRequest as CustomAxiosRequestConfig

    const authBearerToken =
      originalRequest?.headers?.Authorization?.replace('Bearer ', '') ?? ''

    if (
      isTokenExpired(
        store.getState().session.refreshToken ?? authBearerToken
      ) &&
      !customRequest._retry &&
      environment.authProvider === AUTH_PROVIDER.MSAL
    ) {
      console.error('Access token expired, triggering MSAL refresh...')
      customRequest._retry = true

      try {
        if (tokenRefreshPromise) {
          console.log('Token refresh already in progress, awaiting...')
          const newToken = await tokenRefreshPromise
          if (newToken) {
            dynamicParamConfig(
              customRequest,
              LoginTypeEnum.UNAUTHORIZED,
              UserTypeEnum.AGENT
            )
            customRequest.headers['Authorization'] = `Bearer ${newToken}`
            return axiosInstance({ ...customRequest, params: undefined })
          } else return Promise.reject(error)
        }

        console.log('Starting new token refresh...')
        tokenRefreshPromise = refreshMSALToken()
        const newToken = await tokenRefreshPromise

        if (newToken) {
          dynamicParamConfig(
            customRequest,
            LoginTypeEnum.UNAUTHORIZED,
            UserTypeEnum.AGENT
          )
          customRequest.headers['Authorization'] = `Bearer ${newToken}`
          return axiosInstance({ ...customRequest, params: undefined })
        } else {
          console.error('Token refresh failed, rejecting request')
          return Promise.reject(error)
        }
      } finally {
        tokenRefreshPromise = null
        console.log('Token refresh lock cleared')
      }
    }

    // Cognito fallback
    if (
      (error.response?.status === 409 || error.response?.status === 403) &&
      !customRequest._retry &&
      environment.authProvider === AUTH_PROVIDER.COGNITO
    ) {
      customRequest._retry = true
      const refreshToken = store.getState().session.refreshToken
      const clientId = environment.msalConfig.clientId
      const loginUrl = environment.msalConfig.authorityUrl
      if (checkIfRefreshTokenExist(refreshToken, clientId, loginUrl)) {
        try {
          const params = new URLSearchParams()
          params.append('grant_type', 'refresh_token')
          params.append('client_id', clientId)
          params.append('refresh_token', refreshToken as string)
          const tokenResponse = await fetch(`${loginUrl}/oauth2/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: params.toString(),
          })
          if (tokenResponse.ok) {
            const data = await tokenResponse.json()
            if (data.access_token) {
              store.dispatch(updateToken({ token: data.access_token }))
              storeRefreshTokenInRedux(data)
              customRequest.headers[
                'Authorization'
              ] = `Bearer ${data.access_token}`
              return axiosInstance({ ...customRequest, params: undefined })
            }
          }
        } catch (e) {
          console.error('Cognito token refresh error:', e)
        }
      }
    }

    return Promise.reject(
      error instanceof Error ? error : new Error(String(error))
    )
  }
)

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────
const checkIfRefreshTokenExist = (
  refreshToken: string | null | undefined,
  clientId: string,
  loginUrl: string
) => {
  return refreshToken && clientId && loginUrl
}

const storeRefreshTokenInRedux = (data: {
  refresh_token: { refreshToken: string }
}) => {
  if (data.refresh_token) store.dispatch(updateRefreshToken(data.refresh_token))
}

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token)
    const currentTime = Date.now() / 1000
    return typeof decoded.exp === 'number' ? decoded.exp < currentTime : false
  } catch (error) {
    console.error('Failed to decode token:', error)
    return true
  }
}
